import { appBase } from '@tnn/core'

/**
 * Job QR codes. The printed job sheet carries `<origin>/tms/j/<id>`: scanned with the
 * phone's own camera it opens the app, and the in-app scanner reads the same text.
 * Sheets printed before the apps were one say `tms.<domain>/j/<id>`: that address
 * sends the camera on to the new one (deploy/railway/Caddyfile), and the scanner reads both.
 */
export const jobLink = (id: string) => `${window.location.origin}${appBase('tms')}/j/${id}`

/** The QR on a printed waybill: opens the waybill in the app */
export const waybillLink = (id: string) => `${window.location.origin}${appBase('tms')}/w/${id}`

/**
 * The company website's tracking page (thananan.co/track/<code>, apps/website), no account: the QR on the customer's
 * copy, the link staff send them. The website's address comes with the build (`VITE_SITE_URL`, from the web service's
 * SITE_HOST: deploy/railway/web.Dockerfile); in development it is the website's dev server. A build without it links
 * to Thananan OS's own host, which has no such page: build with SITE_HOST (docs/deploy.md)
 */
const siteUrl: string = import.meta.env.VITE_SITE_URL || (import.meta.env.DEV ? 'http://localhost:4321' : window.location.origin)
export const trackLink = (code: string) => `${siteUrl}/track/${code}`

/** The shipping terms on the company website (thananan.co/terms, apps/website): a version by its number, or the one in effect */
export const termsLink = (number?: string) => `${siteUrl}/terms${number ? `?v=${encodeURIComponent(number)}` : ''}`

const ID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

/** The shipment id inside a scanned code, or undefined when it is not one of our job sheets. */
export function jobIdFromCode(text: string): string | undefined {
  const link = text.match(/\/j\/([^/?#\s]+)/)?.[1] ?? text.trim()
  return ID.test(link) ? link.match(ID)?.[0] : undefined
}

/**
 * What a scan of a paper waybill means: the bill id in its QR (`…/w/<id>`), the tracking code in the QR on the
 * customer's copy (`…/track/<code>`), else a bill number read off it ("W6910-0012"); undefined for any other code (a
 * job sheet, a LINE link). The API finds the bill from any of them
 */
export function billCodeFromScan(text: string): string | undefined {
  const tracked = text.match(/\/track\/([0-9A-Za-z]{6,32})(?:[/?#\s]|$)/)?.[1]
  if (tracked) return tracked.toUpperCase()
  const link = text.match(/\/w\/([^/?#\s]+)/)?.[1]
  if (link) return ID.test(link) ? link.match(ID)?.[0] : undefined
  const bare = text.trim()
  if (/^W\d{4}-\d{4,}$/i.test(bare)) return bare.toUpperCase()
  return /^[0-9a-f-]{36}$/i.test(bare) && ID.test(bare) ? bare : undefined
}
